import React, { useContext, useRef, useState, useCallback } from 'react';
import { history, useIntl } from 'umi';
import { StudioContext } from '@/pages/home/pages/studio/context';
import {
  INavStudioGameMenuItem,
  IStudioLeftMenuProps,
} from '@/pages/home/pages/studio/type';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { LANGUAGE_EN } from '@/pages/home/_config/config';
import { buildStudioGamePath, buildTabChildPath, getStudioMenuPath, isTabContainerActive, matchStudioMenuPath, parseStudioUrlInfo } from '../../utils';
import { selectValByCondition } from '@home/utils/common';
import './index.scss';
import { PAGE_TYPE_OUT_PATH, PAGE_TYPE_OUT_URL } from '@/pages/dashboard/_config/constant';
import { buriedPoint, typeIdObj } from '@home/utils/buriedPoint';
import CommonBubble from '@home/components/CommonBubble';
import FoldButton from '@/pages/dashboard/components/FoldButton';
import { useTipsContent } from './hooks';
import classNames from 'classnames';

const StudioLeftMenu = (props: IStudioLeftMenuProps) => {
  const { } = props;
  const { realPath } = parseStudioUrlInfo(history.location.pathname);
  const { locale } = useIntl();
  const { currentGame, activeDashboardList, activeTab, hasResearchList } = useContext(StudioContext);
  const activeItemRef = useRef<HTMLDivElement | null>(null);
  const resizeTimerRef = useRef<number | null>(null);

  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [isHovering, setIsHovering] = useState<boolean>(false);

  const handleTransitionEnd: React.TransitionEventHandler<HTMLDivElement> = useCallback((e) => {
    if (e.propertyName !== 'width') return;
    if (resizeTimerRef.current) {
      window.clearTimeout(resizeTimerRef.current);
    }
    resizeTimerRef.current = window.setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
      resizeTimerRef.current = null;
    }, 100);
  }, []);

  const handleToggleCollapse = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsCollapsed(prev => !prev);
  }, []);

  // const { formatMessage } = useIntl();

  const {tipsContentMap, getValueByKey} = useTipsContent();
  const handleClickMenu = (path: string, menu: INavStudioGameMenuItem) => {
    const { url, u_id: id, name_en: nameEn, name_cn: nameCn, } = menu || {};
    const code = currentGame?.game_code || currentGame?.unified_edition_id;
    const logObj = {
      menuId: id,
      pageName: nameEn || nameCn,
      extInfo: nameEn || nameCn,
      pagePath: url,
      uid: code || '',
      typeId: typeIdObj.studio,
      gameName: currentGame?.game_name,
    };
    // out path打开系统path
    if (menu.page_type === PAGE_TYPE_OUT_PATH) {
      buriedPoint({ ...logObj, pageId: '101401' });
      window.open(`${url}`);
      return;
    }
    // out url打开外部url
    if (menu.page_type === PAGE_TYPE_OUT_URL) {
      buriedPoint({ ...logObj, pageId: '101501' });
      window.open(`${url}`);
      return;
    }
    // display_mode=tab 容器：跳转到第一个子 tab 的带前缀路径
    if (menu.display_mode === 'tab' && menu.tabs?.length) {
      const firstTab = menu.tabs[0];
      const firstTabPath = buildTabChildPath(menu?.name_en, firstTab);
      history.push(
        buildStudioGamePath(
          currentGame?.studio_code,
          code,
          firstTabPath,
          activeTab,
          hasResearchList,
        ),
      );
      return;
    }
    if (path === 'cbt') {
      buriedPoint({ ...logObj, pageId: '101601' });
    }
    history.push(
      buildStudioGamePath(
        currentGame?.studio_code,
        code,
        path,
        activeTab,
        hasResearchList,
      ),
    );
  };

  // 滚动条定位
  // useEffect(() => {
  //   if (activeItemRef.current) {
  //     activeItemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  //   }
  // }, [realPath]);

  return (
    <div
      className={classNames('dbh-studio-context-tab', {
        'dbh-studio-context-tab--collapsed': isCollapsed,
        'dbh-studio-context-tab--hovering': isHovering,
      })}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onTransitionEnd={handleTransitionEnd}
    >
      <div className="dbh-studio-context-auto">
        {(activeDashboardList || currentGame?.dashboard_list || [])?.map(
          (dashboard: INavStudioGameMenuItem, index: number) => {
            const { icon, name_cn: nameCn, name_en: nameEn, default_icon: defaultIcon, active_icon: activeIcon } = dashboard || {};
            const name = selectValByCondition(locale === LANGUAGE_EN, nameEn, nameCn);
            const path = getStudioMenuPath(dashboard);
            const isActive = dashboard.display_mode === 'tab'
              ? isTabContainerActive(dashboard.name_en, realPath)
              : matchStudioMenuPath(dashboard, realPath);
            const isOutPath = dashboard.page_type === PAGE_TYPE_OUT_PATH || dashboard.page_type === PAGE_TYPE_OUT_URL;
            const outPath = dashboard?.url;
            // GTDR热更，设计没有时间提供规则的icon，前端先处理样式
            const isGtdr = path?.includes('gtdrAssessment');
            const iconClassName = classNames('dbh-studio-context-tab-item-icon', {
              'dbh-studio-context-tab-item-icon-gtdr': isGtdr,
            });
            const iconUrl = isActive ? activeIcon || defaultIcon : defaultIcon;

            const itemContent = (
              <div
                key={index}
                className={classNames('dbh-studio-context-tab-item', { active: isActive })}
                onClick={() => {
                  handleClickMenu(path, dashboard);
                }}
                ref={isActive ? activeItemRef : null}
              >
                <div className="dbh-studio-context-tab-item-box">
                  {iconUrl ? (
                    <span className={classNames('svg-icon', iconClassName)}>
                      <img src={iconUrl} alt="" />
                    </span>
                  ) : (
                    icon && (
                      <SvgIcon
                        type={isActive ? `action-${icon}` : icon}
                        className={iconClassName}
                      />
                    )
                  )}
                </div>
                {!isCollapsed && (
                  <span className="dbh-studio-context-tab-item-text dbh-studio-menu-item-text">
                    {name}
                    {
                      isOutPath && <CommonBubble content={tipsContentMap?.[outPath] || getValueByKey(outPath, tipsContentMap) || ''}>
                        <span className="dbh-studio-context-tab-item-right-icon">
                          <SvgIcon
                            type='share-link'
                          />
                        </span>
                      </CommonBubble>
                    }
                  </span>
                )}
              </div>
            );

            if (isCollapsed) {
              return (
                <CommonBubble overlayClassName={classNames('dbh-studio-context-tab-item-bubble--is-collapsed', {})} key={index} content={name} placement="right">
                  {itemContent}
                </CommonBubble>
              );
            }
            return <React.Fragment key={index}>{itemContent}</React.Fragment>;
          },
        )}
      </div>
      <div className="dbh-studio-collapse-btn" onClick={handleToggleCollapse}>
        <FoldButton isExpand={!isCollapsed} />
      </div>
    </div>
  );
};

export default StudioLeftMenu;
